import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { PROFILE_ENDPOINT } from '../config/api';
import './OnboardingModal.css';

function OnboardingModal({ onComplete }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [genderOther, setGenderOther] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [primaryRole, setPrimaryRole] = useState('');
  const [livingSituation, setLivingSituation] = useState('');
  const [relationshipStatus, setRelationshipStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const genderOptions = [
    'Male',
    'Female',
    'Non-binary',
    'Other',
    'Prefer not to say'
  ];

  const ageRangeOptions = [
    '20s',
    '30s',
    '40s',
    '50s',
    '60+'
  ];

  const primaryRoleOptions = [
    'Student',
    'Working Professional',
    'Parent',
    'Caregiver',
    'Retired',
    'Other'
  ];

  const livingSituationOptions = [
    'Alone',
    'With Partner',
    'With Family',
    'With Roommates',
    'With Parents',
    'Other'
  ];

  const relationshipStatusOptions = [
    'Single',
    'In Relationship',
    'Married',
    'Divorced',
    'Widowed',
    'Complicated'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!firstName.trim() || !lastName.trim() || !gender) {
      setError('Please fill in all required fields');
      return;
    }

    if (gender === 'Other' && !genderOther.trim()) {
      setError('Please specify your gender');
      return;
    }

    setLoading(true);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const requestBody = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender: gender,
        terms_accepted: true
      };

      if (gender === 'Other') {
        requestBody.gender_other = genderOther.trim();
      }

      // Add optional extended profile fields
      if (ageRange) {
        requestBody.age_range = ageRange.toLowerCase().replace(' ', '_');
      }
      if (primaryRole) {
        requestBody.primary_role = primaryRole.toLowerCase().replace(/ /g, '_');
      }
      if (livingSituation) {
        requestBody.living_situation = livingSituation.toLowerCase().replace(/ /g, '_');
      }
      if (relationshipStatus) {
        requestBody.relationship_status = relationshipStatus.toLowerCase().replace(/ /g, '_');
      }

      const response = await fetch(PROFILE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save profile');
      }

      const data = await response.json();
      
      // Handle Lambda proxy response (statusCode wrapper)
      if (data.statusCode && data.statusCode !== 200) {
        const errorBody = JSON.parse(data.body);
        throw new Error(errorBody.error || 'Failed to save profile');
      }
      
      const profileData = data.statusCode ? JSON.parse(data.body) : data;

      // Call onComplete callback
      onComplete();
    } catch (err) {
      console.error('Profile save error:', err);
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        <div className="onboarding-header">
          <h1>Welcome to 3AM AI</h1>
          <p>Let's get to know you a bit</p>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              maxLength={100}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender *</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                if (e.target.value !== 'Other') {
                  setGenderOther('');
                }
              }}
              required
            >
              <option value="">Select your gender</option>
              {genderOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {gender === 'Other' && (
            <div className="form-group gender-other-group">
              <label htmlFor="genderOther">Please specify *</label>
              <input
                type="text"
                id="genderOther"
                value={genderOther}
                onChange={(e) => setGenderOther(e.target.value)}
                placeholder="Enter your gender"
                maxLength={100}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="ageRange">Age Range (Optional)</label>
            <select
              id="ageRange"
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {ageRangeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="primaryRole">Primary Role (Optional)</label>
            <select
              id="primaryRole"
              value={primaryRole}
              onChange={(e) => setPrimaryRole(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {primaryRoleOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="livingSituation">Living Situation (Optional)</label>
            <select
              id="livingSituation"
              value={livingSituation}
              onChange={(e) => setLivingSituation(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {livingSituationOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="relationshipStatus">Relationship Status (Optional)</label>
            <select
              id="relationshipStatus"
              value={relationshipStatus}
              onChange={(e) => setRelationshipStatus(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {relationshipStatusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="continue-btn"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OnboardingModal;
